import { useState } from 'react';
import { ArrowUpRight, Check, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';
import type { ConnectionFormat } from '../../../../lib/external-agents/types';
import { ANTHROPIC_KEYS_URL, MANAGED_AGENTS_DOCS_URL } from '../constants';

/**
 * How to connect each kind of agent, shown inside the app. Every example
 * matches what PromptSafe actually sends and reads; keep them in step with
 * supabase/functions/_shared/external-agents.
 */
export type GuideId = 'claude' | ConnectionFormat;

const GUIDE_TABS: { id: GuideId; label: string }[] = [
  { id: 'claude', label: 'Claude Managed Agents' },
  { id: 'json', label: 'Plain JSON' },
  { id: 'openai', label: 'OpenAI-compatible' },
  { id: 'anthropic', label: 'Anthropic-compatible' },
];

export function ConnectionGuideDialog({
  open,
  onOpenChange,
  initialGuide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialGuide: GuideId;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto bg-prompt-safecomfantasy">
        <DialogHeader>
          <DialogTitle>How to connect your agent</DialogTitle>
          <DialogDescription>
            What each connection needs, and exactly what PromptSafe sends and
            expects back.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue={initialGuide} className="flex flex-col gap-4">
          <TabsList className="h-auto flex-wrap justify-start self-start">
            {GUIDE_TABS.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {GUIDE_TABS.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0">
              <Guide id={tab.id} />
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/** One guide, for the dialog or inline in the connection form. */
export function Guide({ id }: { id: GuideId }) {
  switch (id) {
    case 'claude':
      return <ClaudeGuide />;
    case 'json':
      return <JsonGuide />;
    case 'openai':
      return <OpenAIGuide />;
    case 'anthropic':
      return <AnthropicGuide />;
  }
}

function ClaudeGuide() {
  return (
    <GuideLayout>
      <p>
        For agents you built with Claude Managed Agents in Claude Console.
        PromptSafe starts one session per simulation and talks to the agent
        through Anthropic's API. The agent's prompt and tools stay in your
        account.
      </p>
      <Section title="Steps">
        <ol className="flex list-decimal flex-col gap-2 pl-5">
          <li>
            Build your agent and an environment for it in Claude Console. See{' '}
            <ExternalLink href={MANAGED_AGENTS_DOCS_URL}>
              Anthropic's Managed Agents docs
            </ExternalLink>
            .
          </li>
          <li>
            Create an API key in{' '}
            <ExternalLink href={ANTHROPIC_KEYS_URL}>
              Claude Console
            </ExternalLink>
            . A workspace admin saves it once, under <b>API keys</b>. Everyone
            in the workspace uses it.
          </li>
          <li>
            Click <b>Import an agent</b>, pick the agent, then choose the
            environment, a spend limit per simulation, and whether tools that
            need approval are allowed.
          </li>
          <li>
            Optional: <b>Say hello</b> sends one test message, so you know it
            works before running a simulation.
          </li>
        </ol>
      </Section>
      <Section title="Good to know">
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          <li>
            PromptSafe pins the version you import. When you change the agent,
            the list shows <b>vN available</b>, and you choose when to update.
          </li>
          <li>
            Use a test environment. Simulations send many messages, and your
            agent may act on them.
          </li>
          <li>
            Anthropic stops a simulation at its spend limit. You can raise the
            limit and continue from the simulation page.
          </li>
          <li>
            Tools that need approval are refused unless you allow them. Custom
            tools are reported as unavailable, because they run on your side.
          </li>
          <li>
            Each simulation appears in Claude Console as a session called
            "PromptSafe simulation …".
          </li>
        </ul>
      </Section>
    </GuideLayout>
  );
}

function JsonGuide() {
  return (
    <GuideLayout>
      <p>
        For a bot behind your own HTTPS API. PromptSafe sends each message as a
        JSON <Code>POST</Code> and reads the reply from your JSON. Any language
        or framework works.
      </p>

      <Section title="What your endpoint needs">
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          <li>
            A public HTTPS URL with a hostname. IP addresses, private network
            addresses and redirects are refused.
          </li>
          <li>It checks a secret: a Bearer token, or a header you name.</li>
          <li>It replies with JSON within 60 seconds, up to 1 MB.</li>
          <li>
            Ideally a test copy of your bot, not production. Simulations send
            many messages.
          </li>
        </ul>
      </Section>

      <Section title="Session ID (the default)">
        <p>
          Only the latest message is sent, with a session ID your bot uses to
          remember the rest. Either your endpoint issues the ID in its first
          reply, or PromptSafe creates one per simulation.
        </p>
        <CodeBlock
          label="What PromptSafe sends"
          code={`POST https://bot.example.com/chat
Authorization: Bearer <your secret>
Content-Type: application/json
X-PromptSafe-Request-Id: 1ac4e7d5-…:3

{ "message": "Where is my order?", "session_id": "conv_8c1d…" }`}
        />
        <CodeBlock
          label="Your first reply, when your endpoint issues the ID"
          code={`{
  "response": "Happy to help. What's the order number?",
  "conversation_id": "conv_8c1d…"
}`}
        />
        <p className="text-xs">
          Tell PromptSafe where the ID is, here <Code>conversation_id</Code>. On
          the first message there is no ID yet, so <Code>session_id</Code> is
          left out. After that, every reply only needs <Code>response</Code>.
        </p>
      </Section>

      <Section title="Full history each time">
        <p>
          For bots that don't keep memory: every request carries the whole
          conversation so far.
        </p>
        <CodeBlock
          label="What PromptSafe sends"
          code={`{
  "messages": [
    { "role": "user", "content": "Where is my order?" },
    { "role": "assistant", "content": "Could you share your order number?" },
    { "role": "user", "content": "It's 1234." }
  ]
}`}
        />
        <CodeBlock
          label="What PromptSafe expects back"
          code={`{ "response": "Order 1234 ships on Monday." }`}
        />
        <p className="text-xs">
          <Code>user</Code> is the simulated customer, <Code>assistant</Code> is
          your bot. The opening message you set in PromptSafe isn't sent: your
          bot never said it, so the conversation starts with the user.
        </p>
      </Section>

      <Section title="Your own request shape">
        <p>
          Under <b>Request shape and reply location</b>, write the JSON your API
          already takes. Placeholders are replaced as whole values:
        </p>
        <ul className="flex list-disc flex-col gap-1 pl-5">
          <li>
            <Code>{'"{{messages}}"'}</Code>: the conversation, as role and
            content pairs
          </li>
          <li>
            <Code>{'"{{message}}"'}</Code>: the latest message
          </li>
          <li>
            <Code>{'"{{session_id}}"'}</Code>: the session ID
          </li>
          <li>
            <Code>{'"{{model}}"'}</Code>: not used for plain JSON
          </li>
        </ul>
        <CodeBlock
          label="Request template"
          code={`{ "input": { "text": "{{message}}" }, "thread": "{{session_id}}" }`}
        />
        <CodeBlock
          label="Reply path output.text reads the reply from"
          code={`{ "output": { "text": "Your order ships Monday." } }`}
        />
      </Section>

      <Section title="Good to know">
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          <li>
            PromptSafe never retries a message on its own.{' '}
            <Code>X-PromptSafe-Request-Id</Code> helps you find a turn in your
            logs.
          </li>
          <li>
            The connection test must pass before you can save. Change anything
            later and it has to pass again.
          </li>
        </ul>
      </Section>
    </GuideLayout>
  );
}

function OpenAIGuide() {
  return (
    <GuideLayout>
      <p>
        For servers that speak OpenAI's chat format: vLLM, LiteLLM, Ollama or LM
        Studio behind HTTPS, most AI gateways, and OpenAI itself.
      </p>
      <Section title="What you enter">
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          <li>
            <b>Base URL</b>, like <Code>https://llm.example.com/v1</Code>.
            PromptSafe adds <Code>/chat/completions</Code>.
          </li>
          <li>
            <b>API key</b>, sent as <Code>Authorization: Bearer</Code>.
          </li>
          <li>
            <b>Model</b>, exactly as your server expects it.
          </li>
        </ul>
      </Section>
      <Section title="What PromptSafe sends and reads">
        <CodeBlock
          label="Request"
          code={`POST https://llm.example.com/v1/chat/completions
Authorization: Bearer <your key>

{
  "model": "llama-3.3-70b",
  "messages": [
    { "role": "user", "content": "Where is my order?" }
  ]
}`}
        />
        <CodeBlock
          label="Reply, read from choices.0.message.content"
          code={`{ "choices": [{ "message": { "role": "assistant", "content": "…" } }] }`}
        />
        <p className="text-xs">
          The conversation starts with the user: the opening message you set in
          PromptSafe isn't sent. PromptSafe doesn't add a system prompt either.
          Your server adds its own instructions, so the bot behaves as it does
          for your customers.
        </p>
      </Section>
      <Section title="Quick test with OpenAI">
        <p>
          Base URL <Code>https://api.openai.com/v1</Code>, your OpenAI key, and
          a model like <Code>gpt-5.4-mini</Code>. This is the plain model with
          no instructions, which is useful to check the setup, not to test a
          real bot.
        </p>
      </Section>
    </GuideLayout>
  );
}

function AnthropicGuide() {
  return (
    <GuideLayout>
      <p>
        For servers that speak Anthropic's Messages format, like a gateway in
        front of Claude, or Anthropic itself.
      </p>
      <Section title="What you enter">
        <ul className="flex list-disc flex-col gap-1.5 pl-5">
          <li>
            <b>Base URL</b>, like <Code>https://gateway.example.com</Code>.
            PromptSafe adds <Code>/v1/messages</Code>, or only{' '}
            <Code>/messages</Code> when the URL ends in <Code>/v1</Code>.
          </li>
          <li>
            <b>API key</b>, sent as <Code>x-api-key</Code>, with{' '}
            <Code>anthropic-version: 2023-06-01</Code>.
          </li>
          <li>
            <b>Model</b>, like <Code>claude-sonnet-5</Code>.
          </li>
        </ul>
      </Section>
      <Section title="What PromptSafe sends and reads">
        <CodeBlock
          label="Request"
          code={`POST https://gateway.example.com/v1/messages
x-api-key: <your key>
anthropic-version: 2023-06-01

{
  "model": "claude-sonnet-5",
  "max_tokens": 1024,
  "messages": [
    { "role": "user", "content": "Where is my order?" }
  ]
}`}
        />
        <CodeBlock
          label="Reply, read from the text blocks in content"
          code={`{ "content": [{ "type": "text", "text": "…" }] }`}
        />
        <p className="text-xs">
          The Messages format must start with the user, so the opening message
          isn't sent. PromptSafe doesn't add a system prompt either: your
          gateway adds its own instructions.
        </p>
      </Section>
      <Section title="Quick test with Anthropic">
        <p>
          Base URL <Code>https://api.anthropic.com</Code>, your Anthropic key,
          and <Code>claude-haiku-4-5</Code>. This is plain Claude with no
          instructions, which is useful to check the setup, not to test a real
          bot. To test an agent you built in Claude Console, use{' '}
          <b>Claude Managed Agents</b> instead.
        </p>
      </Section>
    </GuideLayout>
  );
}

function GuideLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex max-w-[70ch] flex-col gap-5 text-sm leading-6 text-prompt-safecomabbey">
      {children}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-prompt-safecomvulcan">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded border border-[#00000014] bg-prompt-safecomwhite px-1 py-px font-mono text-[12px] text-prompt-safecomvulcan">
      {children}
    </code>
  );
}

function ExternalLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 font-medium text-prompt-safecomoutrageous-orange hover:underline"
    >
      {children}
      <ArrowUpRight className="h-3 w-3" />
    </a>
  );
}

function CodeBlock({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});

  return (
    <figure className="m-0 flex flex-col gap-1">
      <figcaption className="flex items-center justify-between text-xs text-prompt-safecomabbey">
        {label}
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs hover:bg-prompt-safecomwhite"
          aria-label={`Copy: ${label}`}
        >
          {copied ? (
            <Check className="h-3 w-3" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </figcaption>
      <pre className="m-0 overflow-x-auto rounded-lg border border-[#00000014] bg-prompt-safecomwhite p-3 font-mono text-[12px] leading-5 text-prompt-safecomvulcan">
        {code}
      </pre>
    </figure>
  );
}
